import ImpressionHandler from './ImpressionHandler';

import GtmHelper from '@tracking/gtm-helper';
import { isEmpty } from '@utilities/collection-utils';
import { watchInviewNodes } from '@utilities/dom';
import { impressionScrollThreshold, impressionTimeThreshold } from '@utilities/WindowContext';

type TrackingCallback = (element: HTMLElement) => unknown;

/**
 * Register a new GTM Service instance
 * @param selector DOM elements selector
 * @param trackingCallback callback to be called when elements are intersected
 * @param exitCallback callback to be called when the page is unloaded
 * @param scrollThreshold scroll threshold in percentage
 * @param timeThreshold time threshold in milliseconds
 */
const registerImpressionInstance = (
  selector: string,
  trackingCallback: TrackingCallback,
  scrollThreshold?: number,
  timeThreshold?: number,
) =>
  watchInviewNodes(selector, trackingCallback, null, {
    intersectionObserverOptions: { rootMargin: '0% 0%', threshold: [(scrollThreshold ?? 0) / 100, 1] },
    delay: timeThreshold,
  });

const cardTrackingObserver = registerImpressionInstance(
  '.js-tracking-card',
  (card) => {
    if (!card.dataset.tracking || card.dataset.tracked || card.dataset.trackOnlyClick === 'true') return;

    const data = JSON.parse(card.dataset.tracking) as Record<string, any>;
    if (isEmpty(data)) return;

    const impression = GtmHelper.getCardTrackingData(card, data);
    const handler = ImpressionHandler.getInstance('cards', GtmHelper.gtmCardImpression);
    handler.add(impression);

    card.dataset.tracked = 'true';
  },
  impressionScrollThreshold,
  impressionTimeThreshold,
);

const sponsorTrackingObserver = registerImpressionInstance(
  '.js-tracking-sponsor',
  (promo) => {
    if (!promo.dataset.tracking || promo.dataset.tracked) return;
    const data = JSON.parse(promo.dataset.tracking) as object;

    if (isEmpty(data)) return;

    const handler = ImpressionHandler.getInstance('sponsors', GtmHelper.gtmSponsorImpression);
    handler.add(data);

    promo.dataset.tracked = 'true';
  },
  impressionScrollThreshold,
  impressionTimeThreshold,
);

// MANUAL TRACKING (for React)

export const trackCardElement =
  (tracking: Record<string, any>, trackOnlyImpression?: boolean) => (element: HTMLElement) => {
    if (!tracking || !element) return;

    element.dataset.tracking = JSON.stringify(tracking);
    element.dataset.trackingType = 'card';
    element.dataset.trackOnlyImpression = trackOnlyImpression ? 'true' : 'false';

    cardTrackingObserver.observe(element);

    return () => {
      cardTrackingObserver.unobserve(element);
    };
  };

export const trackSponsorElement =
  (tracking: Record<string, any>, trackOnlyImpression?: boolean) => (element: HTMLElement) => {
    if (!tracking || !element) return;

    element.dataset.tracking = JSON.stringify(tracking);
    element.dataset.trackingType = 'sponsor';
    element.dataset.trackOnlyImpression = trackOnlyImpression ? 'true' : 'false';

    sponsorTrackingObserver.observe(element);

    return () => {
      sponsorTrackingObserver.unobserve(element);
    };
  };

// ████████╗██████╗  █████╗  ██████╗██╗  ██╗    ██╗███╗   ██╗████████╗███████╗██████╗  █████╗  ██████╗████████╗██╗ ██████╗ ███╗   ██╗███████╗
// ╚══██╔══╝██╔══██╗██╔══██╗██╔════╝██║ ██╔╝    ██║████╗  ██║╚══██╔══╝██╔════╝██╔══██╗██╔══██╗██╔════╝╚══██╔══╝██║██╔═══██╗████╗  ██║██╔════╝
//    ██║   ██████╔╝███████║██║     █████╔╝     ██║██╔██╗ ██║   ██║   █████╗  ██████╔╝███████║██║        ██║   ██║██║   ██║██╔██╗ ██║███████╗
//    ██║   ██╔══██╗██╔══██║██║     ██╔═██╗     ██║██║╚██╗██║   ██║   ██╔══╝  ██╔══██╗██╔══██║██║        ██║   ██║██║   ██║██║╚██╗██║╚════██║
//    ██║   ██║  ██║██║  ██║╚██████╗██║  ██╗    ██║██║ ╚████║   ██║   ███████╗██║  ██║██║  ██║╚██████╗   ██║   ██║╚██████╔╝██║ ╚████║███████║
//    ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝╚═╝  ╚═╝    ╚═╝╚═╝  ╚═══╝   ╚═╝   ╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝   ╚═╝   ╚═╝ ╚═════╝ ╚═╝  ╚═══╝╚══════╝
//

const trackingHandlers = {
  card: GtmHelper.gtmCardElementClick,
  cta: GtmHelper.gtmCtaClick,
  link: GtmHelper.gtmLinkClick,
  sponsor: GtmHelper.gtmSponsorClick,
} as const;
export type TrackingType = keyof typeof trackingHandlers;

const findElementToTrack = (target: HTMLElement) => {
  const el = target.matches('[data-tracking]') ? target : target.closest<HTMLElement>('[data-tracking]');
  if (!el || !(el instanceof HTMLElement)) return null;

  if (el.dataset.tracking && el.dataset.trackOnlyImpression !== 'true') {
    return el;
  }

  return findElementToTrack(el.parentElement);
};

const classRegExp = /js-tracking-([a-z]+)/;
const trackInteraction = (e: MouseEvent) => {
  if (e.button > 1) return; // ignore right-clicks (context menu)

  const target = e.target as HTMLElement;

  const el = findElementToTrack(target);
  if (!el || !(el instanceof HTMLElement)) return null;

  const trackingType = el.dataset.trackingType || (classRegExp.exec(el.className)?.[1] as TrackingType);
  const handler = trackingHandlers[trackingType];

  const data = {
    ...(JSON.parse(el.dataset.tracking) as object),
  };

  if (!handler || isEmpty(data)) return;

  handler(el, data);
};

document.addEventListener('click', trackInteraction, { capture: true });
document.addEventListener('auxclick', trackInteraction, { capture: true });
